import React, {RefObject, useState} from 'react';
import {
  JAVASCRIPT_VOID,
  LIVE_AFREECATV_DOMAIN,
  MAX_TITLE_LEN,
} from 'constants/config';
import {Ibbs} from 'lib/api/vodInfo';
import {IWebEditorContents} from 'lib/api/setWebEditorJob';
import Dim from 'components/common/Dim';
import useScript from 'hooks/useScript';
import {t, getLocale} from 'lib/i18n';

/**
 * 영상 저장 내용 포함하는
 * 모달 컴포넌트
 */

interface ISaveContentsProps {
  marginState: string;
  popupWrapRef: RefObject<HTMLDivElement>;
  bbsInfo: Ibbs[] | null | undefined;
  webEditSaveInfo: IWebEditorContents;
  langs: {
    [key: string]: string;
  };
  setWebEditSaveInfo: React.Dispatch<React.SetStateAction<IWebEditorContents>>;
  onClickSaveCancel: () => void;
  onClickSave: () => void;
}

const SaveContents = ({
  marginState,
  popupWrapRef,
  bbsInfo,
  webEditSaveInfo,
  langs,
  setWebEditSaveInfo,
  onClickSaveCancel,
  onClickSave,
}: ISaveContentsProps) => {
  const [bbsState, setBbsState] = useState(''); // 게시판 선택
  const [bbsTitle, setBbsTitle] = useState(t('게시판 선택')); // 게시판 이름
  const [categoryState, setCategoryState] = useState(''); // 카테고리 선택
  const [categoryTitle, setCategoryTitle] = useState(t('카테고리 선택')); // 카테고리 이름
  const [vodCategoryIdx, setVodCategoryIdx] = useState(-1); // 상세 카테고리 idx
  const [vodCategoryState, setVodCategoryState] = useState(''); // 상세 카테고리 선택
  const [vodCategoryTitle, setVodCategoryTitle] = useState(t('카테고리 선택')); // 상세 카테고리 이름
  const [isLangOpen, setIsLangOpen] = useState(false); // 언어 선택
  const [langTitle, setLangTitle] = useState(t('언어 선택')); // 언어 이름
  const [scriptLoaded, scriptError] = useScript(
    `${LIVE_AFREECATV_DOMAIN}/script/locale/${getLocale()}/vod_editor_category.js`,
  ); // 카테고리 선택 스크립트 호출

  /**
   * 게시판 리스트 클릭 함수
   */
  const onClickBbsList = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();

    if (bbsState === '') {
      setCategoryState(''); // 카테고리 리스트 닫음
      setVodCategoryState(''); // 상세 카테고리 리스트 닫음
      setBbsState('on'); // 게시판 리스트 Open
    } else {
      setBbsState('');
    }
  };
  /**
   * 게시판 선택 함수
   */
  const onClickBbs = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();

    const bbs = Number(e.currentTarget.dataset.bbs_type);
    if (!bbs) return;

    const bbsText = e.currentTarget.innerText;

    setWebEditSaveInfo({
      ...webEditSaveInfo,
      bbs,
    });
    setBbsTitle(bbsText); // 게시판 제목 셋팅
    setBbsState(''); // 게시판 리스트 박스 닫음
  };

  /**
   * 카테고리 리스트 선택 함수
   */
  const onClickCategoryList = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();

    if (categoryState === '') {
      setBbsState(''); // 게시판 리스트 박스 닫음
      setVodCategoryState(''); // 상세 카테고리 리스트 닫음
      setCategoryState('on');
    } else {
      setCategoryState('');
    }
  };

  /**
   * 상세 카테고리 리스트 선택 함수
   */
  const onClickVodCategoryList = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();

    if (vodCategoryState === '') {
      setBbsState(''); // 게시판 리스트 박스 닫음
      setCategoryState(''); // 카테고리 리스트 닫음
      setVodCategoryState('on');
    } else {
      setVodCategoryState('');
    }
  };

  /**
   * 카테고리 클릭 함수
   */
  const onClickCategory = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();

    const category = e.currentTarget.dataset.ucc_cate;
    let vod_category = e.currentTarget.dataset.cate_no;
    if (!category || !vod_category) return;

    const categoryText = e.currentTarget.innerText;
    const childIdx = Number(e.currentTarget.dataset.child_idx);

    if (childIdx > 0) {
      // 상세 카테고리 있으면 빈값으로 만듬(상세 카테고리 선택하도록)
      vod_category = '';
    }

    setWebEditSaveInfo({
      ...webEditSaveInfo,
      category,
      vod_category,
    });
    setCategoryTitle(categoryText); // 카테고리 제목 셋팅
    setCategoryState(''); // 카테고리 리스트 박스 닫음
    setVodCategoryTitle(t('카테고리 선택')); // 상세 카테고리 초기화
    setVodCategoryIdx(childIdx); // 상세 카테고리 셋팅
  };

  /**
   * 상세 카테고리 클릭 함수
   */
  const onClickVodCategory = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();

    const category = e.currentTarget.dataset.ucc_cate;
    const vod_category = e.currentTarget.dataset.cate_no;
    if (!category || !vod_category) return;

    const categoryText = e.currentTarget.innerText;

    setWebEditSaveInfo({
      ...webEditSaveInfo,
      category,
      vod_category,
    });
    setVodCategoryTitle(categoryText); // 카테고리 제목 셋팅
    setVodCategoryState(''); // 카테고리 리스트 박스 닫음
  };

  /**
   * 게시글 제목 입력 함수
   */
  const onChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebEditSaveInfo({
      ...webEditSaveInfo,
      title: e.target.value,
    });
  };

  /**
   * 게시글 내용 입력 함수
   */
  const onChangeContents = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWebEditSaveInfo({
      ...webEditSaveInfo,
      contents: e.target.value,
    });
  };

  /**
   * 언어 선택 함수
   */
  const onClickLanguage = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();

    const languageCode = e.currentTarget.dataset.language_code;
    if (!languageCode) return;

    setWebEditSaveInfo({
      ...webEditSaveInfo,
      strmLangType: languageCode,
    });

    const langInfo = langs[languageCode];
    if (!langInfo) return;

    setLangTitle(langInfo); // 언어 제목 셋팅
    setIsLangOpen(false); // 언어 리스트 닫음
  };

  /**
   * 언어 리스트 선택 함수
   */
  const onClickLanguageList = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();
    setIsLangOpen((prev: boolean) => !prev); // 언어 리스트 Open / Close
  };

  return (
    <>
      <Dim onClick={onClickSaveCancel} />
      <div
        className='popup_wrap'
        ref={popupWrapRef}
        style={{
          // maxWidth: "600px",
          margin: marginState,
        }}>
        <div className='popup_contents'>
          <div className='save_info'>
            <dl>
              <dt>
                {t('게시판 선택')}
                <em>＊</em>
              </dt>
              <dd>
                <div className='select_wrap'>
                  <div>
                    <a
                      href={JAVASCRIPT_VOID}
                      className={bbsState}
                      onClick={onClickBbsList}>
                      {bbsTitle}
                    </a>
                    <ul>
                      {bbsInfo &&
                        bbsInfo.map((bbs, idx) => (
                          <li key={idx}>
                            <a
                              href={JAVASCRIPT_VOID}
                              data-bbs_type={bbs.bbs_no}
                              onClick={onClickBbs}>
                              {bbs.name}
                            </a>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
                <br />
                <div className='select_wrap'>
                  <div>
                    <a
                      href={JAVASCRIPT_VOID}
                      className={categoryState}
                      onClick={onClickCategoryList}>
                      {categoryTitle}
                    </a>
                    {scriptLoaded && !scriptError && (
                      <ul>
                        {szVodCategory &&
                          szVodCategory.CHANNEL.VOD_CATEGORY.map(
                            (val: any, idx: number) => (
                              <li key={idx}>
                                <a
                                  href={JAVASCRIPT_VOID}
                                  data-ucc_cate={val.ucc_cate}
                                  data-cate_no={val.cate_no}
                                  data-child_idx={
                                    val.child.length > 0 ? idx : -1
                                  }
                                  onClick={onClickCategory}>
                                  {val.cate_name}
                                </a>
                              </li>
                            ),
                          )}
                        ,
                      </ul>
                    )}
                  </div>
                </div>
                {vodCategoryIdx !== -1 && (
                  <div className='select_wrap'>
                    <div>
                      <a
                        href={JAVASCRIPT_VOID}
                        className={vodCategoryState}
                        onClick={onClickVodCategoryList}>
                        {vodCategoryTitle}
                      </a>
                      {scriptLoaded && !scriptError && (
                        <ul>
                          {szVodCategory &&
                            szVodCategory.CHANNEL.VOD_CATEGORY[
                              vodCategoryIdx
                            ].child.map((val: any, idx: number) => (
                              <li key={idx}>
                                <a
                                  href={JAVASCRIPT_VOID}
                                  data-ucc_cate={val.ucc_cate}
                                  data-cate_no={val.cate_no}
                                  onClick={onClickVodCategory}>
                                  {val.cate_name}
                                </a>
                              </li>
                            ))}
                          ,
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </dd>
            </dl>
            <dl>
              <dt>
                {t('언어 선택')}
                <em>＊</em>
              </dt>
              <dd>
                <div className='select_wrap'>
                  <div>
                    <a
                      href={JAVASCRIPT_VOID}
                      className={isLangOpen ? 'on' : ''}
                      onClick={onClickLanguageList}>
                      {langTitle}
                    </a>
                    <ul>
                      {Object.entries(langs).map(([code, name]) => (
                        <li key={code}>
                          <a
                            href={JAVASCRIPT_VOID}
                            onClick={onClickLanguage}
                            data-language_code={code}>
                            {name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </dd>
            </dl>
            <dl>
              <dt>
                {t('제목')}
                <em>＊</em>
              </dt>
              <dd>
                <input
                  type='text'
                  value={webEditSaveInfo.title}
                  placeholder={t('제목을 입력해주세요.')}
                  maxLength={MAX_TITLE_LEN}
                  onChange={onChangeTitle}
                />
              </dd>
            </dl>
            <dl>
              <dt>{t('내용')}</dt>
              <dd>
                <textarea
                  value={webEditSaveInfo.contents}
                  onChange={onChangeContents}
                />
              </dd>
            </dl>
            <p>
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    '※ ＊표시된 영역은 필수 입력입니다 <br/>※ 본 영상에서 발생되는 별풍선 및 애드벌룬 수익은 방송한 스트리머에게 전달되며,<br/>광고 수익은 로그인,비로그인 유저가 재생한 경우 편집한 유저에게 10% 배분됩니다.',
                  ),
                }}
              />
            </p>
          </div>
        </div>
        <div className='btn_wrap'>
          <button type='button' className='btn1' onClick={onClickSaveCancel}>
            {t('취소')}
          </button>
          <button type='button' className='btn2' onClick={onClickSave}>
            {t('저장')}
          </button>
        </div>
        <a
          href={JAVASCRIPT_VOID}
          className='popup_close'
          onClick={onClickSaveCancel}>
          popup close
        </a>
      </div>
    </>
  );
};

export default SaveContents;
